import { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Button, Alert, TextField, Typography, Divider,
  Dialog, DialogContent, DialogActions, Tooltip, Collapse,
  MenuItem, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatCard } from "@/components/StatCard";
import { StatusPill } from "@/components/StatusPill";
import { ServicePicker } from "@/components/ServicePicker";
import { ServiceGroupPicker } from "@/components/ServiceGroupPicker";
import { UomPicker } from "@/components/UomPicker";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  serviceChargesApi, admissionTypesApi, personTypesApi, insuranceSchemesApi, currenciesApi,
  branchesApi,
  type ServiceChargeDto, type ServiceDto, type ServiceGroupDto, type UomDto, type BranchDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Money without a hard-coded currency — a charge row may be quoted in any of them. */
const amount = (n: number, code?: string | null) =>
  `${code ? `${code} ` : ""}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Service charge rate card. One row = what one service costs for one audience
 * (admission type × person type × insurance scheme) over one date window, split into the
 * professional and facility shares.
 *
 * Two things this screen has to make obvious, because they are what the data model is for:
 *  - a rate is never overwritten — "Revise" closes the current row and opens a successor, so a
 *    back-dated bill still resolves the rate that applied then;
 *  - several rows can match one patient, so the Resolve panel shows which one actually wins.
 */
export function ServiceChargesPage() {
  const qc = useQueryClient();
  const { taxLabel } = useTaxRegime();

  const [serviceItem, setServiceItem] = useState<ServiceDto | null>(null);
  const [group, setGroup] = useState<ServiceGroupDto | null>(null);
  const [admissionTypeId, setAdmissionTypeId] = useState("");
  const [personTypeId, setPersonTypeId] = useState("");
  const [insuranceSchemeId, setInsuranceSchemeId] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceChargeDto | null>(null);
  const [revising, setRevising] = useState<ServiceChargeDto | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);

  const canManage = useAuth(s => s.hasPermission("sales.servicecharge.manage"));

  const dimensions = useDimensionMasters();

  const filters = {
    serviceItemId: serviceItem?.id,
    serviceGroupId: group?.id,
    admissionTypeId: admissionTypeId || undefined,
    personTypeId: personTypeId || undefined,
    insuranceSchemeId: insuranceSchemeId || undefined,
    // Default view is the live rate card; unchecking shows closed rows too.
    effectiveOn: showHistory ? undefined : today(),
  };

  const query = useQuery({
    queryKey: ["service-charges", filters, pagination.page, pagination.pageSize],
    queryFn: () => serviceChargesApi.list({
      ...filters, page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Charge row deleted" },
    mutationFn: (id: string) => serviceChargesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-charges"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: ServiceChargeDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: ServiceChargeDto) => {
    if (row.effectiveFrom <= today()) {
      alert("This rate has already taken effect and may have been billed. Revise it or close it with an effective-to date instead of deleting.");
      return;
    }
    if (confirm(`Delete the ${amount(row.rate, row.currencyCode)} rate for "${row.serviceItemName}" starting ${row.effectiveFrom}?`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const rows = query.data?.items ?? [];
  const stats = useMemo(() => {
    const open = rows.filter(r => !r.effectiveTo).length;
    const upcoming = rows.filter(r => r.effectiveFrom > today()).length;
    const targeted = rows.filter(r => r.admissionTypeId || r.personTypeId || r.insuranceSchemeId).length;
    return { open, upcoming, targeted };
  }, [rows]);

  const columns: GridColDef<ServiceChargeDto>[] = [
    { field: "serviceItemName", headerName: "Service", flex: 1.4, minWidth: 200 },
    { field: "serviceGroupName", headerName: "Group", width: 150,
      valueGetter: (_v, row) => row.serviceGroupName || "—" },
    {
      field: "audience", headerName: "Applies to", flex: 1.2, minWidth: 200, sortable: false,
      renderCell: (p) => <AudienceCell row={p.row} />,
    },
    {
      field: "rate", headerName: "Rate", width: 130, align: "right", headerAlign: "right",
      renderCell: (p) => amount(p.row.rate, p.row.currencyCode),
    },
    {
      field: "split", headerName: "Prof. / facility", width: 160, align: "right", headerAlign: "right",
      sortable: false,
      renderCell: (p) => {
        const { professionalCharge: pro, facilityCharge: fac } = p.row;
        if (pro === 0 && fac === 0) return <Box component="span" sx={{ color: "text.disabled" }}>unsplit</Box>;
        return `${pro.toFixed(2)} / ${fac.toFixed(2)}`;
      },
    },
    {
      field: "taxRatePct", headerName: taxLabel, width: 90, align: "right", headerAlign: "right",
      valueGetter: (_v, row) => (row.isTaxable ? `${row.taxRatePct}%` : "—"),
    },
    { field: "unitName", headerName: "Unit", width: 100, valueGetter: (_v, row) => row.unitName || "—" },
    {
      field: "branchId", headerName: "Scope", width: 150, sortable: false,
      renderCell: (p) => {
        if (!p.row.branchId) {
          return <Box component="span" sx={{ color: "text.secondary" }}>All branches</Box>;
        }
        const branch = dimensions.branches.find(b => b.id === p.row.branchId);
        return <StatusPill label={branch ? branch.code : "One branch"} tone="info" />;
      },
    },
    {
      field: "effectiveFrom", headerName: "Effective", width: 200, sortable: false,
      renderCell: (p) => <EffectiveCell row={p.row} />,
    },
    {
      field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          // Revise leads, and both tooltips keep the distinction the menu used to spell out:
          // revising closes this row and opens a successor, editing only fixes a typo.
          {
            label: "Revise rate — closes this row, opens a new one",
            icon: <PriceChangeIcon fontSize="small" />, hidden: !canManage,
            onClick: () => setRevising(p.row),
          },
          {
            label: "Edit — correct a data-entry mistake",
            icon: <EditIcon fontSize="small" />, hidden: !canManage,
            onClick: () => openEdit(p.row),
          },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Service Charges"
        subtitle="The rate card: what each service costs, for whom, and from when. Rates are never overwritten — a change closes the old row and opens a new one."
        actions={
          <>
            <Button variant="outlined" startIcon={<CalculateIcon />} onClick={() => setResolveOpen(true)}>
              Check a rate
            </Button>
            {canManage && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New charge</Button>
            )}
          </>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <StatCard label="Rows shown" value={query.data?.totalCount ?? 0}
          hint={showHistory ? "full history" : "live today"} />
        <StatCard label="Open-ended" value={stats.open} hint="no end date — the current rate" />
        <StatCard label="Upcoming" value={stats.upcoming} severity={stats.upcoming ? "info" : "default"}
          hint="starts after today" />
        <StatCard label="Targeted" value={stats.targeted}
          hint="pinned to an admission/person/insurance" />
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <ServicePicker label="Service" value={serviceItem} onChange={setServiceItem} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ServiceGroupPicker value={group} onChange={setGroup} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControlLabel
              control={<Switch checked={showHistory} onChange={(_, v) => setShowHistory(v)} />}
              label="Full history"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DimensionSelect label="Admission type" value={admissionTypeId} onChange={setAdmissionTypeId}
              options={dimensions.admissionTypes} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DimensionSelect label="Person type" value={personTypeId} onChange={setPersonTypeId}
              options={dimensions.personTypes} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DimensionSelect label="Insurance" value={insuranceSchemeId} onChange={setInsuranceSchemeId}
              options={dimensions.insuranceSchemes} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
            getRowClassName={(p) => (p.row.effectiveTo && p.row.effectiveTo < today() ? "row-closed" : "")}
            sx={{ "& .row-closed": { opacity: 0.6 } }}
          />
        </Box>
      </Paper>

      <ChargeDialog
        open={formOpen} onClose={() => setFormOpen(false)} row={editing}
        defaultService={serviceItem} dimensions={dimensions}
      />
      <ReviseDialog open={!!revising} onClose={() => setRevising(null)} row={revising} />
      <ResolveDialog
        open={resolveOpen} onClose={() => setResolveOpen(false)}
        defaultService={serviceItem} dimensions={dimensions}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------------------------

interface DimensionOption { id: string; code: string; name: string }
interface Dimensions {
  admissionTypes: DimensionOption[];
  personTypes: DimensionOption[];
  insuranceSchemes: DimensionOption[];
  currencies: { id: string; code: string; name: string; isBaseCurrency: boolean }[];
  branches: BranchDto[];
}

/** The small masters every dialog on this page needs. Cached — they change rarely. */
function useDimensionMasters(): Dimensions {
  const common = { staleTime: 5 * 60_000 };
  const admission = useQuery({
    queryKey: ["admission-types", "options"],
    queryFn: () => admissionTypesApi.list({ isActive: true, pageSize: 200 }), ...common,
  });
  const person = useQuery({
    queryKey: ["person-types", "options"],
    queryFn: () => personTypesApi.list({ isActive: true, pageSize: 200 }), ...common,
  });
  const insurance = useQuery({
    queryKey: ["insurance-schemes", "options"],
    queryFn: () => insuranceSchemesApi.list({ isActive: true, pageSize: 200 }), ...common,
  });
  const currency = useQuery({
    queryKey: ["currencies", "options"],
    queryFn: () => currenciesApi.list({ isActive: true, pageSize: 100 }), ...common,
  });
  const branches = useQuery({
    queryKey: ["branches"], queryFn: branchesApi.list, ...common,
  });

  return {
    admissionTypes: admission.data?.items ?? [],
    personTypes: person.data?.items ?? [],
    insuranceSchemes: insurance.data?.items ?? [],
    currencies: currency.data?.items ?? [],
    branches: branches.data ?? [],
  };
}

/** "Any" is a real, meaningful choice here — it is the wildcard the base rate row uses. */
function DimensionSelect({
  label, value, onChange, options, helperText,
}: {
  label: string; value: string; onChange: (v: string) => void;
  options: DimensionOption[]; helperText?: string;
}) {
  return (
    <TextField select label={label} size="small" fullWidth
      value={value} onChange={(e) => onChange(e.target.value)} helperText={helperText}>
      <MenuItem value=""><em>Any</em></MenuItem>
      {options.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  );
}

function AudienceCell({ row }: { row: ServiceChargeDto }) {
  const parts = [row.admissionTypeName, row.personTypeName, row.insuranceSchemeName].filter(Boolean);
  if (parts.length === 0) {
    return (
      <Tooltip title="Base rate — applies when no narrower row matches">
        <Box component="span" sx={{ color: "text.secondary" }}>Everyone</Box>
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
      {row.insuranceSchemeName && <StatusPill label={row.insuranceSchemeName} tone="info" />}
      {row.personTypeName && <StatusPill label={row.personTypeName} tone="neutral" />}
      {row.admissionTypeName && <StatusPill label={row.admissionTypeName} tone="neutral" />}
    </Stack>
  );
}

function EffectiveCell({ row }: { row: ServiceChargeDto }) {
  const t = today();
  const upcoming = row.effectiveFrom > t;
  const closed = !!row.effectiveTo && row.effectiveTo < t;
  return (
    <Stack spacing={0.25} sx={{ py: 0.5 }}>
      <Typography variant="body2">
        {row.effectiveFrom} → {row.effectiveTo ?? "open"}
      </Typography>
      {upcoming && <StatusPill label="Upcoming" tone="info" />}
      {closed && <StatusPill label="Closed" tone="neutral" />}
      {!upcoming && !closed && <StatusPill label="Current" tone="success" />}
    </Stack>
  );
}

// ---------------------------------------------------------------------------------------------
// Create / edit
// ---------------------------------------------------------------------------------------------

function emptyCharge(): ChargeFormState {
  return {
    serviceItemId: "", rate: "", professionalCharge: "0", facilityCharge: "0",
    effectiveFrom: today(), effectiveTo: "",
    unitOfMeasureId: "",
    admissionTypeId: "", personTypeId: "", insuranceSchemeId: "",
    isTaxable: false, taxRatePct: "0",
    currencyId: "",
    branchId: "",
    isActive: true,
  };
}

interface ChargeFormState {
  serviceItemId: string;
  rate: string; professionalCharge: string; facilityCharge: string;
  effectiveFrom: string; effectiveTo: string;
  unitOfMeasureId: string;
  admissionTypeId: string; personTypeId: string; insuranceSchemeId: string;
  isTaxable: boolean; taxRatePct: string;
  currencyId: string;
  /** "" = applies to every sub-tenant. Set only at creation — see the dialog note. */
  branchId: string;
  isActive: boolean;
}

function ChargeDialog({
  open, onClose, row, defaultService, dimensions,
}: {
  open: boolean; onClose: () => void; row: ServiceChargeDto | null;
  defaultService: ServiceDto | null; dimensions: Dimensions;
}) {
  const qc = useQueryClient();
  const { taxLabel } = useTaxRegime();
  const isEdit = !!row;
  const [service, setService] = useState<ServiceDto | null>(null);
  const [uom, setUom] = useState<UomDto | null>(null);
  const [f, setF] = useState<ChargeFormState>(emptyCharge());

  useEffect(() => {
    if (!open) return;
    setUom(null);
    if (row) {
      setService(null);
      setF({
        serviceItemId: row.serviceItemId,
        rate: String(row.rate),
        professionalCharge: String(row.professionalCharge),
        facilityCharge: String(row.facilityCharge),
        effectiveFrom: row.effectiveFrom,
        effectiveTo: row.effectiveTo ?? "",
        unitOfMeasureId: row.unitOfMeasureId ?? "",
        admissionTypeId: row.admissionTypeId ?? "",
        personTypeId: row.personTypeId ?? "",
        insuranceSchemeId: row.insuranceSchemeId ?? "",
        isTaxable: row.isTaxable,
        taxRatePct: String(row.taxRatePct),
        currencyId: row.currencyId ?? "",
        branchId: row.branchId ?? "",
        isActive: row.isActive,
      });
    } else {
      setService(defaultService);
      setF({ ...emptyCharge(), serviceItemId: defaultService?.id ?? "" });
    }
  }, [open, row, defaultService]);

  // Default a new charge to the base currency. Kept out of the reset above so it still lands when
  // the currency master finishes loading after the dialog opened, and never stomps an edit.
  const baseCurrencyId = dimensions.currencies.find(c => c.isBaseCurrency)?.id ?? "";
  useEffect(() => {
    if (!open || row || !baseCurrencyId) return;
    setF(prev => (prev.currencyId ? prev : { ...prev, currencyId: baseCurrencyId }));
  }, [open, row, baseCurrencyId]);

  const set = <K extends keyof ChargeFormState>(k: K, v: ChargeFormState[K]) =>
    setF(prev => ({ ...prev, [k]: v }));

  const rate = Number(f.rate || 0);
  const pro = Number(f.professionalCharge || 0);
  const fac = Number(f.facilityCharge || 0);
  const taxPct = f.isTaxable ? Number(f.taxRatePct || 0) : 0;
  const taxAmount = Math.round((rate * taxPct) / 100 * 100) / 100;

  const err =
    !f.serviceItemId ? "Pick a service"
    : f.rate === "" || rate < 0 ? "Rate must be zero or more"
    : pro + fac > rate ? `Professional + facility (${(pro + fac).toFixed(2)}) cannot exceed the rate (${rate.toFixed(2)})`
    : !f.effectiveFrom ? "Effective-from required"
    : f.effectiveTo && f.effectiveTo < f.effectiveFrom ? "Effective-to cannot precede effective-from"
    : f.isTaxable && taxPct <= 0 ? `A taxable charge needs a ${taxLabel} rate above zero`
    : null;

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Charge updated" : "Charge created" },
    mutationFn: async () => {
      const payload = {
        serviceItemId: f.serviceItemId,
        rate, professionalCharge: pro, facilityCharge: fac,
        effectiveFrom: f.effectiveFrom,
        effectiveTo: f.effectiveTo || null,
        unitOfMeasureId: f.unitOfMeasureId || null,
        admissionTypeId: f.admissionTypeId || null,
        personTypeId: f.personTypeId || null,
        insuranceSchemeId: f.insuranceSchemeId || null,
        isTaxable: f.isTaxable, taxRatePct: taxPct,
        currencyId: f.currencyId || null,
        isActive: f.isActive,
      };
      // Branch scope is part of a row's identity — the update command deliberately has no
      // BranchId, so a re-scope means a new row rather than a silent move.
      if (isEdit && row) { await serviceChargesApi.update(row.id, payload); return row.id; }
      const r = await serviceChargesApi.create({ ...payload, branchId: f.branchId || null });
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-charges"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? `Edit charge — ${row?.serviceItemName}` : "New service charge"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          {isEdit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Editing rewrites this row in place. If this rate has already been billed against, close
              the dialog and use <strong>Revise rate</strong> instead so the old rate stays on record.
            </Alert>
          )}

          <Typography variant="overline" color="text.secondary">What</Typography>
          <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              {isEdit ? (
                <TextField label="Service" size="small" fullWidth disabled
                  value={row?.serviceItemName ?? ""} helperText="Cannot move a charge to another service" />
              ) : (
                <ServicePicker label="Service" required value={service}
                  onChange={(v) => { setService(v); set("serviceItemId", v?.id ?? ""); }} />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <UomPicker label="Unit" value={uom} initialId={f.unitOfMeasureId || undefined}
                onChange={(v) => { setUom(v); set("unitOfMeasureId", v?.id ?? ""); }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Applies to branch" size="small" fullWidth
                value={f.branchId} onChange={(e) => set("branchId", e.target.value)}
                disabled={isEdit}
                helperText={isEdit
                  ? "Branch scope is fixed — create a new row to price another branch"
                  : "A branch-specific rate beats the all-branches one"}>
                <MenuItem value=""><em>All branches</em></MenuItem>
                {dimensions.branches.filter(b => b.isActive).map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Who it applies to
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Leave a dimension on “Any” to make this the fallback rate. When several rows match a
            patient, insurance beats person type beats admission type.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DimensionSelect label="Admission type" value={f.admissionTypeId}
                onChange={(v) => set("admissionTypeId", v)} options={dimensions.admissionTypes} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DimensionSelect label="Person type" value={f.personTypeId}
                onChange={(v) => set("personTypeId", v)} options={dimensions.personTypes} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DimensionSelect label="Insurance scheme" value={f.insuranceSchemeId}
                onChange={(v) => set("insuranceSchemeId", v)} options={dimensions.insuranceSchemes} />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            How much
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Rate *" type="number" size="small" fullWidth
                value={f.rate} onChange={(e) => set("rate", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select label="Currency" size="small" fullWidth
                value={f.currencyId} onChange={(e) => set("currencyId", e.target.value)}>
                <MenuItem value=""><em>Unspecified</em></MenuItem>
                {dimensions.currencies.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.code}{c.isBaseCurrency ? " (base)" : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Professional share" type="number" size="small" fullWidth
                value={f.professionalCharge} onChange={(e) => set("professionalCharge", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                helperText="Doctor's cut of the rate" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Facility share" type="number" size="small" fullWidth
                value={f.facilityCharge} onChange={(e) => set("facilityCharge", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                helperText={`Unallocated: ${(rate - pro - fac).toFixed(2)}`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel label={`${taxLabel} applicable`}
                control={
                  <Switch checked={f.isTaxable}
                    onChange={(_, v) => setF(prev => ({ ...prev, isTaxable: v, taxRatePct: v ? prev.taxRatePct : "0" }))} />
                } />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label={`${taxLabel} rate (%)`} type="number" size="small" fullWidth
                value={f.taxRatePct} onChange={(e) => set("taxRatePct", e.target.value)}
                disabled={!f.isTaxable} inputProps={{ min: 0, max: 100, step: "0.01" }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Gross (rate + tax)" size="small" fullWidth disabled
                value={(rate + taxAmount).toFixed(2)} />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            When
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Effective from *" type="date" size="small" fullWidth
                value={f.effectiveFrom} onChange={(e) => set("effectiveFrom", e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Effective to" type="date" size="small" fullWidth
                value={f.effectiveTo} onChange={(e) => set("effectiveTo", e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank for the current, open-ended rate" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel label="Active"
                control={<Switch checked={f.isActive} onChange={(_, v) => set("isActive", v)} />} />
            </Grid>
          </Grid>

          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Revise
// ---------------------------------------------------------------------------------------------

function ReviseDialog({
  open, onClose, row,
}: { open: boolean; onClose: () => void; row: ServiceChargeDto | null }) {
  const qc = useQueryClient();
  const [newRate, setNewRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [professionalCharge, setProfessionalCharge] = useState("0");
  const [facilityCharge, setFacilityCharge] = useState("0");

  useEffect(() => {
    if (!open || !row) return;
    setNewRate(String(row.rate));
    // The API requires the successor to start strictly after the current row's start date.
    setEffectiveFrom(row.effectiveFrom >= today() ? addDays(row.effectiveFrom, 1) : today());
    setProfessionalCharge(String(row.professionalCharge));
    setFacilityCharge(String(row.facilityCharge));
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: "Rate revised" },
    mutationFn: () => serviceChargesApi.revise(row!.id, {
      newRate: Number(newRate || 0),
      effectiveFrom,
      professionalCharge: Number(professionalCharge || 0),
      facilityCharge: Number(facilityCharge || 0),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-charges"] });
      onClose();
    },
  });

  if (!row) return null;

  const rate = Number(newRate || 0);
  const pro = Number(professionalCharge || 0);
  const fac = Number(facilityCharge || 0);
  const err =
    newRate === "" || rate < 0 ? "New rate must be zero or more"
    : pro + fac > rate ? "Professional + facility cannot exceed the new rate"
    : effectiveFrom <= row.effectiveFrom ? `Must start after ${row.effectiveFrom}, when the current rate began`
    : row.effectiveTo && row.effectiveTo < effectiveFrom
      ? `This rate already ended on ${row.effectiveTo} — add a new charge row instead`
      : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>Revise rate — {row.serviceItemName}</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

          <Alert severity="info" sx={{ mb: 2 }}>
            The current rate closes on <strong>{effectiveFrom ? addDays(effectiveFrom, -1) : "—"}</strong> and
            the new one runs from <strong>{effectiveFrom || "—"}</strong>. Bills dated before the
            switch keep resolving the old rate.
          </Alert>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">Current</Typography>
            <Typography variant="body2">
              {amount(row.rate, row.currencyCode)} · {row.effectiveFrom} → {row.effectiveTo ?? "open"}
            </Typography>
            <AudienceCell row={row} />
            <Typography variant="caption" color="text.secondary">
              The new row inherits this audience, unit, tax and currency.
            </Typography>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="New rate *" type="number" size="small" fullWidth autoFocus
                value={newRate} onChange={(e) => setNewRate(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Professional share" type="number" size="small" fullWidth
                value={professionalCharge} onChange={(e) => setProfessionalCharge(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Facility share" type="number" size="small" fullWidth
                value={facilityCharge} onChange={(e) => setFacilityCharge(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="New rate effective from *" type="date" size="small" fullWidth
                value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>

          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            Revise
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Resolve — "which rate would this patient actually get?"
// ---------------------------------------------------------------------------------------------

function ResolveDialog({
  open, onClose, defaultService, dimensions,
}: {
  open: boolean; onClose: () => void;
  defaultService: ServiceDto | null; dimensions: Dimensions;
}) {
  const { taxLabel } = useTaxRegime();
  const [service, setService] = useState<ServiceDto | null>(null);
  const [on, setOn] = useState(today());
  const [admissionTypeId, setAdmissionTypeId] = useState("");
  const [personTypeId, setPersonTypeId] = useState("");
  const [insuranceSchemeId, setInsuranceSchemeId] = useState("");

  useEffect(() => {
    if (!open) return;
    setService(defaultService);
    setOn(today());
    setAdmissionTypeId(""); setPersonTypeId(""); setInsuranceSchemeId("");
  }, [open, defaultService]);

  const q = useQuery({
    queryKey: ["service-charge-resolve", service?.id, on, admissionTypeId, personTypeId, insuranceSchemeId],
    queryFn: () => serviceChargesApi.resolve({
      serviceItemId: service!.id,
      on,
      admissionTypeId: admissionTypeId || undefined,
      personTypeId: personTypeId || undefined,
      insuranceSchemeId: insuranceSchemeId || undefined,
    }),
    enabled: open && !!service,
  });

  const r = q.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>Check a rate</ClosableDialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Answers the question billing asks: for this service, this patient profile, on this date —
          which rate wins?
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <ServicePicker label="Service" required value={service} onChange={setService} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="On date" type="date" size="small" fullWidth
              value={on} onChange={(e) => setOn(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DimensionSelect label="Admission type" value={admissionTypeId}
              onChange={setAdmissionTypeId} options={dimensions.admissionTypes} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DimensionSelect label="Person type" value={personTypeId}
              onChange={setPersonTypeId} options={dimensions.personTypes} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DimensionSelect label="Insurance" value={insuranceSchemeId}
              onChange={setInsuranceSchemeId} options={dimensions.insuranceSchemes} />
          </Grid>
        </Grid>

        <Collapse in={!!service}>
          <Box sx={{ mt: 3 }}>
            {q.isLoading && <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>}
            {q.isError && <Alert severity="error">{errorMessage(q.error)}</Alert>}
            {r && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
                  <StatCard label="Rate" value={amount(r.rate, r.currencyCode)}
                    hint={r.unitName ? `per ${r.unitName}` : undefined} />
                  <StatCard label={taxLabel} value={r.isTaxable ? amount(r.taxAmount, r.currencyCode) : "—"}
                    hint={r.isTaxable ? `${r.taxRatePct}%` : "not taxable"} />
                  <StatCard label="Gross" value={amount(r.grossAmount, r.currencyCode)} severity="info" />
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1}>
                  <SourceNote source={r.source} />
                  <DetailRow label="Professional / facility"
                    value={r.professionalCharge === 0 && r.facilityCharge === 0
                      ? "unsplit"
                      : `${r.professionalCharge.toFixed(2)} / ${r.facilityCharge.toFixed(2)}`} />
                  <DetailRow label="Rate window"
                    value={r.effectiveFrom ? `${r.effectiveFrom} → ${r.effectiveTo ?? "open"}` : "—"} />
                  <DetailRow label="Posts to"
                    value={r.glAccountCode ? `${r.glAccountCode} — ${r.glAccountName ?? ""}` : "default service revenue head"} />
                </Stack>
              </Paper>
            )}
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ textAlign: "right" }}>{value}</Typography>
    </Stack>
  );
}

/** Where the number came from matters — a fallback means the rate card has a gap. */
function SourceNote({ source }: { source: "ServiceCharge" | "ServiceRate" | "ServiceDefault" }) {
  if (source === "ServiceCharge") {
    return <Alert severity="success" variant="outlined">Matched a charge row on the rate card.</Alert>;
  }
  if (source === "ServiceRate") {
    return (
      <Alert severity="warning" variant="outlined">
        No charge row matched — fell back to the service's older rate history. Add a charge row to
        price this service properly.
      </Alert>
    );
  }
  return (
    <Alert severity="warning" variant="outlined">
      No charge row and no rate history — fell back to the service's default rate.
    </Alert>
  );
}
